import { formatDateTime } from "@clipboard-health/date-time";
import { Button, Card, Modal, Spin, Tag } from "antd";
import { type AxiosError } from "axios";

import { useGetWorkerScreeningSurvey } from "./api/useGetWorkerScreeningSurvey";
import { useDeleteWorkerScreeningSurvey } from "./api/useDeleteWorkerScreeningSurvey";

interface Props {
  workerId: string;
}

const CARD_TITLE = "Home Health & Hospice Screening Survey";

const FAIL_REASON_LABELS: Record<string, string> = {
  INSUFFICIENT_EXPERIENCE: "Insufficient experience",
  NO_DOCUMENTATION_SYSTEMS: "No documentation (EMR) systems",
  NO_CAR: "No car",
};

function statusTagColor(status: string): string {
  if (status === "PASSED") {
    return "success";
  }

  if (status === "FAILED") {
    return "error";
  }

  return "default";
}

/**
 * Shows a worker's latest Home Health & Hospice screening survey result and lets a Clipboard Health
 * employee reset (hard-delete) it so the worker can re-take it. Rendered next to the Home Health
 * enabled toggle in the worker drawer. A 404 from the API means the worker has not taken the survey
 * yet.
 */
export function HomeHealthScreeningSurveyStatus(props: Props) {
  const { workerId } = props;

  const { data, isLoading, isError, error } = useGetWorkerScreeningSurvey(workerId);
  const { mutateAsync: resetSurvey, isLoading: isResetting } =
    useDeleteWorkerScreeningSurvey(workerId);

  if (isLoading) {
    return <Spin />;
  }

  const isNotTaken = isError && (error as AxiosError)?.response?.status === 404;

  if (isNotTaken) {
    return (
      <Card title={CARD_TITLE}>
        No screening survey on file. The professional has not taken it yet.
      </Card>
    );
  }

  // Unexpected (non-404) errors are surfaced via analytics; keep the drawer uncluttered.
  if (isError || !data) {
    return undefined;
  }

  const { status, failReasons, createdAt } = data.data.attributes;

  function handleReset() {
    Modal.confirm({
      title: "Reset screening survey?",
      content:
        "This permanently deletes the professional's Home Health & Hospice screening survey so they can re-take it. This cannot be undone.",
      okText: "Reset survey",
      okButtonProps: { danger: true },
      cancelText: "Cancel",
      onOk: async () => {
        await resetSurvey();
      },
    });
  }

  return (
    <Card title={CARD_TITLE}>
      <div style={{ marginBottom: 8 }}>
        <strong>Result:</strong> <Tag color={statusTagColor(status)}>{status}</Tag>
      </div>
      {failReasons.length > 0 && (
        <div style={{ marginBottom: 8 }}>
          <strong>Fail reasons:</strong>{" "}
          {failReasons.map((reason) => FAIL_REASON_LABELS[reason] ?? reason).join(", ")}
        </div>
      )}
      <div style={{ marginBottom: 16 }}>
        <strong>Submitted:</strong> {formatDateTime(createdAt)}
      </div>
      <Button danger size="small" loading={isResetting} onClick={handleReset}>
        Reset screening survey
      </Button>
    </Card>
  );
}
